import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { 
  Loader2, Calendar, Save, Calculator, 
  AlertCircle, CheckCircle, FileText,
  ChevronDown, ChevronUp, RefreshCw, ShieldCheck, Clock
} from 'lucide-react';
import { toast } from 'sonner';
import { usePeriodo } from '../../contexts/PeriodoContext';
import { formatarDataCurta, formatarDataBR, isFimDeSemana } from '../../utils/dateUtils';
import { InputDataBR } from '../../components/ui/input-data-br';
import { InputHora } from '../../components/ui/input-hora';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tipos de justificativa disponíveis
const TIPOS_JUSTIFICATIVA = [
  { value: 'compensar_banco_horas', label: 'Compensar no Banco de Horas' },
  { value: 'abono_gerencia', label: 'Abono pela Gerência' },
  { value: 'atestado_medico', label: 'Atestado Médico' },
  { value: 'declaracao_horas', label: 'Declaração de Horas' },
  { value: 'troca_turno', label: 'Troca de Turno' },
  { value: 'servico_externo', label: 'Serviço Externo' },
  { value: 'treinamento', label: 'Treinamento' },
  { value: 'reuniao', label: 'Reunião' },
  { value: 'ajuste_manual', label: 'Ajuste Manual de Ponto' },
  { value: 'outros', label: 'Outros' }
];

const DigitacaoRapida = ({ token, empresaId, empregados }) => {
  const { periodoSelecionado, alterarPeriodo } = usePeriodo();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reprocessando, setReprocessando] = useState(false);
  const [showConfirmReprocessar, setShowConfirmReprocessar] = useState(false);
  const [empregadoId, setEmpregadoId] = useState('');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [dias, setDias] = useState([]);
  const [diasOriginais, setDiasOriginais] = useState([]);
  const [empregadoInfo, setEmpregadoInfo] = useState(null);
  const [apuracao, setApuracao] = useState(null);
  const [apuracaoLoading, setApuracaoLoading] = useState(false);
  const [showApuracao, setShowApuracao] = useState(false);
  
  // Refs para navegação entre campos com Enter
  const inputRefs = useRef({});
  
  // Novos estados para justificativa e aprovação prévia
  const [justificativaTipo, setJustificativaTipo] = useState('ajuste_manual');
  const [justificativaMotivo, setJustificativaMotivo] = useState('');
  const [jaAprovado, setJaAprovado] = useState(false);

  // Slots na ordem correta para navegação
  const slots = ['entrada1', 'saida1', 'entrada2', 'saida2', 'entrada3', 'saida3'];

  // Função para navegar para próximo campo
  const navigateToNext = (diaIndex, currentSlot) => {
    const slotIndex = slots.indexOf(currentSlot);
    
    // Próximo slot no mesmo dia
    if (slotIndex < slots.length - 1) {
      const nextSlot = slots[slotIndex + 1];
      const key = `${diaIndex}-${nextSlot}`;
      inputRefs.current[key]?.focus();
    } 
    // Primeiro slot do próximo dia
    else if (diaIndex < dias.length - 1) {
      const key = `${diaIndex + 1}-entrada1`;
      inputRefs.current[key]?.focus();
    }
  };

  // Função para navegar para campo anterior
  const navigateToPrev = (diaIndex, currentSlot) => {
    const slotIndex = slots.indexOf(currentSlot);
    
    // Slot anterior no mesmo dia
    if (slotIndex > 0) {
      const prevSlot = slots[slotIndex - 1];
      const key = `${diaIndex}-${prevSlot}`;
      inputRefs.current[key]?.focus();
    } 
    // Último slot do dia anterior
    else if (diaIndex > 0) {
      const key = `${diaIndex - 1}-saida3`;
      inputRefs.current[key]?.focus();
    }
  };

  // Sincronizar com período global
  useEffect(() => {
    if (periodoSelecionado?.data_inicio && periodoSelecionado?.data_fim) {
      setDataInicio(periodoSelecionado.data_inicio);
      setDataFim(periodoSelecionado.data_fim);
    }
  }, [periodoSelecionado]);

  // Atualizar período global quando usuário altera manualmente
  const handleDataInicioChange = (novaData) => {
    setDataInicio(novaData);
    if (novaData && dataFim) {
      alterarPeriodo(novaData, dataFim);
    }
  };

  const handleDataFimChange = (novaData) => {
    setDataFim(novaData);
    if (dataInicio && novaData) {
      alterarPeriodo(dataInicio, novaData);
    }
  };

  // Buscar marcações do período
  const buscarMarcacoes = async () => {
    if (!empregadoId || !dataInicio || !dataFim) {
      toast.error('Selecione empregado e período');
      return;
    }

    setLoading(true);
    setShowApuracao(false);
    setApuracao(null);
    
    try {
      const res = await fetch(
        `${API_URL}/api/marcacoes/digitacao-rapida?empregado_id=${empregadoId}&data_inicio=${dataInicio}&data_fim=${dataFim}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (res.ok) {
        const data = await res.json();
        setDias(data.dias || []);
        setDiasOriginais(JSON.parse(JSON.stringify(data.dias || []))); // Deep copy
        setEmpregadoInfo(data.empregado);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erro ao buscar marcações');
      }
    } catch (err) {
      toast.error('Erro ao buscar marcações');
    } finally {
      setLoading(false);
    }
  };

  // Calcular horas trabalhadas de um dia
  const calcularHorasTrabalhadas = useCallback((dia) => {
    const slots = [
      { entrada: dia.entrada1, saida: dia.saida1 },
      { entrada: dia.entrada2, saida: dia.saida2 },
      { entrada: dia.entrada3, saida: dia.saida3 },
    ];
    
    let totalMinutos = 0;
    
    for (const slot of slots) {
      if (slot.entrada && slot.saida) {
        try {
          const [hE, mE] = slot.entrada.split(':').map(Number);
          const [hS, mS] = slot.saida.split(':').map(Number);
          
          const minutosEntrada = hE * 60 + mE;
          const minutosSaida = hS * 60 + mS;
          
          if (minutosSaida > minutosEntrada) {
            totalMinutos += (minutosSaida - minutosEntrada);
          }
        } catch (e) {
          // Ignora erro de parse
        }
      }
    }
    
    const horas = Math.floor(totalMinutos / 60);
    const minutos = totalMinutos % 60;
    return `${String(horas).padStart(2, '0')}:${String(minutos).padStart(2, '0')}`;
  }, []);

  // Atualizar valor de um campo
  const handleChange = (index, campo, valor) => {
    const novosDias = [...dias];
    novosDias[index] = {
      ...novosDias[index],
      [campo]: valor
    };
    setDias(novosDias);
  };

  // Verificar se dia foi alterado
  const diaFoiAlterado = (dia, index) => {
    const original = diasOriginais[index];
    if (!original) return false;
    
    return dia.entrada1 !== original.entrada1 ||
           dia.saida1 !== original.saida1 ||
           dia.entrada2 !== original.entrada2 ||
           dia.saida2 !== original.saida2 ||
           dia.entrada3 !== original.entrada3 ||
           dia.saida3 !== original.saida3;
  };

  // Salvar e apurar
  const salvarEApurar = async () => {
    if (dias.length === 0) {
      toast.error('Nenhum dia para salvar');
      return;
    }
    
    // Validar justificativa
    if (!justificativaTipo) {
      toast.error('Selecione uma justificativa para a digitação');
      return;
    }

    setSaving(true);
    
    try {
      // Salvar marcações com justificativa e flag de aprovação
      const res = await fetch(`${API_URL}/api/marcacoes/digitacao-rapida`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          empregado_id: empregadoId,
          dias: dias,
          justificativa_tipo: justificativaTipo,
          justificativa_motivo: justificativaMotivo || `Digitação manual - Período ${dataInicio} a ${dataFim}`,
          ja_aprovado: jaAprovado
        })
      });
      
      if (!res.ok) {
        const err = await res.json();
        toast.error(err.detail || 'Erro ao salvar');
        return;
      }
      
      const result = await res.json();
      const msgAprovado = jaAprovado ? ' (já aprovado)' : '';
      toast.success(`Salvo! ${result.estatisticas.marcacoes_criadas} marcações criadas, ${result.estatisticas.dias_processados} dias processados${msgAprovado}`);
      
      // Buscar apuração
      await buscarApuracao();
      setShowApuracao(true);
      
      // Atualizar originais para refletir novo estado
      setDiasOriginais(JSON.parse(JSON.stringify(dias)));
      
    } catch (err) {
      toast.error('Erro ao salvar marcações');
    } finally {
      setSaving(false);
    }
  };

  // Buscar apuração do período
  const buscarApuracao = async () => {
    setApuracaoLoading(true);
    
    try {
      const res = await fetch(
        `${API_URL}/api/marcacoes/apuracao-periodo?empregado_id=${empregadoId}&data_inicio=${dataInicio}&data_fim=${dataFim}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (res.ok) {
        const data = await res.json();
        setApuracao(data);
      }
    } catch (err) {
      console.error('Erro ao buscar apuração:', err);
    } finally {
      setApuracaoLoading(false);
    }
  };

  // Reprocessar período - força o motor a rodar novamente
  const reprocessarPeriodo = async () => {
    if (!empregadoId || !dataInicio || !dataFim) {
      toast.error('Selecione o empregado e o período');
      return;
    }
    
    setReprocessando(true);
    setShowConfirmReprocessar(false);
    
    try {
      const res = await fetch(`${API_URL}/api/marcacoes/reprocessar-periodo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          empregado_id: empregadoId,
          data_inicio: dataInicio,
          data_fim: dataFim
        })
      });
      
      if (!res.ok) {
        const err = await res.json();
        toast.error(err.detail || 'Erro ao reprocessar');
        return;
      }
      
      const result = await res.json();
      toast.success(
        `Reprocessamento concluído! ${result.estatisticas.dias_processados} dias processados, ${result.estatisticas.dias_atualizados} atualizados`
      );
      
      // Atualizar apuração
      await buscarApuracao();
      setShowApuracao(true);
      
    } catch (err) {
      toast.error('Erro ao reprocessar período');
      console.error('Erro:', err);
    } finally {
      setReprocessando(false);
    }
  };

  // Formatar data para exibição (usando utilitário)
  const formatarData = (dataStr) => {
    return formatarDataCurta(dataStr);
  };

  // Verificar se é fim de semana (usando utilitário)
  const verificarFimDeSemana = (dataStr) => {
    return isFimDeSemana(dataStr);
  };

  return (
    <div className="space-y-4">
      {/* Configurações da Digitação */}
      <Card className="border-amber-200 bg-amber-50/30">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-amber-600" />
            Configurações da Digitação
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Justificativa Padrão</Label>
              <Select value={justificativaTipo} onValueChange={setJustificativaTipo}>
                <SelectTrigger data-testid="select-justificativa-tipo">
                  <SelectValue placeholder="Selecione a justificativa..." />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS_JUSTIFICATIVA.map((tipo) => (
                    <SelectItem key={tipo.value} value={tipo.value}>
                      {tipo.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Motivo / Observação</Label>
              <Textarea
                placeholder="Descreva o motivo da digitação manual..."
                value={justificativaMotivo}
                onChange={(e) => setJustificativaMotivo(e.target.value)}
                className="h-[38px] min-h-[38px] resize-none"
                data-testid="input-justificativa-motivo"
              />
            </div>
          </div>
          <div className="mt-4 flex items-center space-x-2">
            <Checkbox 
              id="ja-aprovado" 
              checked={jaAprovado} 
              onCheckedChange={setJaAprovado}
              data-testid="checkbox-ja-aprovado"
            />
            <label
              htmlFor="ja-aprovado"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
            >
              Já aprovado pela chefia
              <span className="text-slate-500 font-normal ml-1">
                (as ocorrências serão geradas com status aprovado)
              </span>
            </label>
          </div>
        </CardContent>
      </Card>

      {/* Filtros */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Digitação Rápida de Apontamentos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-[200px]">
              <Label>Empregado</Label>
              <Select value={empregadoId} onValueChange={setEmpregadoId}>
                <SelectTrigger data-testid="select-empregado-digitacao">
                  <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent>
                  {empregados.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.nome} ({emp.matricula || 'S/M'})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Data Início</Label>
              <InputDataBR
                value={dataInicio}
                onChange={(e) => handleDataInicioChange(e.target.value)}
                className="w-36"
              />
            </div>
            <div>
              <Label>Data Fim</Label>
              <InputDataBR
                value={dataFim}
                onChange={(e) => handleDataFimChange(e.target.value)}
                className="w-36"
              />
            </div>
            <Button onClick={buscarMarcacoes} disabled={loading} data-testid="btn-carregar-digitacao">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Calendar className="w-4 h-4 mr-2" />}
              Carregar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Grade de Digitação */}
      {dias.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base">
                  {empregadoInfo?.nome || 'Empregado'}
                </CardTitle>
                <p className="text-sm text-slate-500">
                  {empregadoInfo?.matricula} • {dias.length} dias
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs">
                  <span className="text-amber-500 mr-1">*</span> = Alterado
                </Badge>
                <Button 
                  variant="outline"
                  onClick={() => setShowConfirmReprocessar(true)} 
                  disabled={reprocessando}
                  data-testid="btn-reprocessar-periodo"
                >
                  {reprocessando ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  Reprocessar
                </Button>
                <Button 
                  onClick={salvarEApurar} 
                  disabled={saving}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Salvar e Apurar
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="w-24 sticky left-0 bg-slate-50 z-10">Data</TableHead>
                    <TableHead className="w-20 text-center">Ent. 1</TableHead>
                    <TableHead className="w-20 text-center">Saí. 1</TableHead>
                    <TableHead className="w-20 text-center">Ent. 2</TableHead>
                    <TableHead className="w-20 text-center">Saí. 2</TableHead>
                    <TableHead className="w-20 text-center">Ent. 3</TableHead>
                    <TableHead className="w-20 text-center">Saí. 3</TableHead>
                    <TableHead className="w-24 text-center">Trabalhado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dias.map((dia, index) => {
                    const alterado = diaFoiAlterado(dia, index);
                    const fds = verificarFimDeSemana(dia.data);
                    
                    return (
                      <TableRow 
                        key={dia.data} 
                        className={`${fds ? 'bg-slate-100/50' : ''} ${alterado ? 'bg-amber-50/50' : ''}`}
                      >
                        <TableCell className={`font-medium sticky left-0 z-10 ${fds ? 'bg-slate-100/50' : alterado ? 'bg-amber-50/50' : 'bg-white'}`}>
                          <div className="flex items-center gap-1">
                            {alterado && <span className="text-amber-500 font-bold">*</span>}
                            <span className={fds ? 'text-slate-400' : ''}>
                              {dia.dia_semana} {formatarData(dia.data)}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="p-1">
                          <InputHora
                            ref={el => inputRefs.current[`${index}-entrada1`] = el}
                            value={dia.entrada1 || ''}
                            onChange={(e) => handleChange(index, 'entrada1', e.target.value)}
                            onEnter={() => navigateToNext(index, 'entrada1')}
                            onBackEmpty={() => navigateToPrev(index, 'entrada1')}
                            className="h-8 text-sm"
                          />
                        </TableCell>
                        <TableCell className="p-1">
                          <InputHora
                            ref={el => inputRefs.current[`${index}-saida1`] = el}
                            value={dia.saida1 || ''}
                            onChange={(e) => handleChange(index, 'saida1', e.target.value)}
                            onEnter={() => navigateToNext(index, 'saida1')}
                            onBackEmpty={() => navigateToPrev(index, 'saida1')}
                            className="h-8 text-sm"
                          />
                        </TableCell>
                        <TableCell className="p-1">
                          <InputHora
                            ref={el => inputRefs.current[`${index}-entrada2`] = el}
                            value={dia.entrada2 || ''}
                            onChange={(e) => handleChange(index, 'entrada2', e.target.value)}
                            onEnter={() => navigateToNext(index, 'entrada2')}
                            onBackEmpty={() => navigateToPrev(index, 'entrada2')}
                            className="h-8 text-sm"
                          />
                        </TableCell>
                        <TableCell className="p-1">
                          <InputHora
                            ref={el => inputRefs.current[`${index}-saida2`] = el}
                            value={dia.saida2 || ''}
                            onChange={(e) => handleChange(index, 'saida2', e.target.value)}
                            onEnter={() => navigateToNext(index, 'saida2')}
                            onBackEmpty={() => navigateToPrev(index, 'saida2')}
                            className="h-8 text-sm"
                          />
                        </TableCell>
                        <TableCell className="p-1">
                          <InputHora
                            ref={el => inputRefs.current[`${index}-entrada3`] = el}
                            value={dia.entrada3 || ''}
                            onChange={(e) => handleChange(index, 'entrada3', e.target.value)}
                            onEnter={() => navigateToNext(index, 'entrada3')}
                            onBackEmpty={() => navigateToPrev(index, 'entrada3')}
                            className="h-8 text-sm"
                          />
                        </TableCell>
                        <TableCell className="p-1">
                          <InputHora
                            ref={el => inputRefs.current[`${index}-saida3`] = el}
                            value={dia.saida3 || ''}
                            onChange={(e) => handleChange(index, 'saida3', e.target.value)}
                            onEnter={() => navigateToNext(index, 'saida3')}
                            onBackEmpty={() => navigateToPrev(index, 'saida3')}
                            className="h-8 text-sm"
                          />
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline" className="font-mono">
                            {calcularHorasTrabalhadas(dia)}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Resumo da Apuração */}
      {showApuracao && (
        <Card className="border-emerald-200 bg-emerald-50/30">
          <CardHeader className="pb-2">
            <div 
              className="flex items-center justify-between cursor-pointer"
              onClick={() => setShowApuracao(!showApuracao)}
            >
              <CardTitle className="text-base flex items-center gap-2 text-emerald-700">
                <Calculator className="w-4 h-4" />
                Resumo da Apuração
              </CardTitle>
              {showApuracao ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </div>
          </CardHeader>
          <CardContent>
            {apuracaoLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
              </div>
            ) : apuracao ? (
              <div className="space-y-4">
                {/* Totalizadores */}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  <div className="bg-white rounded-lg p-3 border">
                    <p className="text-xs text-slate-500">Dias Trabalhados</p>
                    <p className="text-2xl font-bold text-slate-800">{apuracao.totais?.dias_trabalhados || 0}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border">
                    <p className="text-xs text-slate-500">Horas Normais</p>
                    <p className="text-2xl font-bold text-blue-600">{apuracao.totais?.horas_normais || '00:00'}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border">
                    <p className="text-xs text-slate-500">Extras 50%</p>
                    <p className="text-2xl font-bold text-amber-600">{apuracao.totais?.horas_extras_50 || '00:00'}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border">
                    <p className="text-xs text-slate-500">Extras 100%</p>
                    <p className="text-2xl font-bold text-orange-600">{apuracao.totais?.horas_extras_100 || '00:00'}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border">
                    <p className="text-xs text-slate-500">Adicional Noturno</p>
                    <p className="text-2xl font-bold text-purple-600">{apuracao.totais?.adicional_noturno || '00:00'}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border">
                    <p className="text-xs text-slate-500">Faltas</p>
                    <p className="text-2xl font-bold text-red-600">{apuracao.totais?.dias_falta || 0}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border">
                    <p className="text-xs text-slate-500">Atrasos</p>
                    <p className="text-2xl font-bold text-rose-600">{apuracao.totais?.atrasos || '00:00'}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border">
                    <p className="text-xs text-slate-500">Saídas Antecipadas</p>
                    <p className="text-2xl font-bold text-rose-600">{apuracao.totais?.saidas_antecipadas || '00:00'}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border col-span-2">
                    <p className="text-xs text-slate-500">Saldo Banco de Horas</p>
                    <p className={`text-2xl font-bold ${apuracao.totais?.banco_horas_saldo >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {apuracao.totais?.banco_horas_saldo >= 0 ? '+' : ''}{apuracao.totais?.banco_horas_saldo || 0}h
                    </p>
                  </div>
                </div>

                {/* Detalhes por dia */}
                {apuracao.detalhes_dias?.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm font-medium text-slate-700 mb-2">Detalhes por Dia</p>
                    <div className="max-h-60 overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Data</TableHead>
                            <TableHead>Situação</TableHead>
                            <TableHead>Trabalhado</TableHead>
                            <TableHead>Extras</TableHead>
                            <TableHead>Noturno</TableHead>
                            <TableHead>Observações</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {apuracao.detalhes_dias.map((dia, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="font-mono text-sm">
                                {formatarData(dia.data)}
                              </TableCell>
                              <TableCell>
                                <Badge 
                                  variant={dia.situacao === 'falta' ? 'destructive' : 'outline'}
                                  className="text-xs"
                                >
                                  {dia.situacao || 'normal'}
                                </Badge>
                              </TableCell>
                              <TableCell className="font-mono">
                                {dia.horas_trabalhadas?.toFixed(2) || '0.00'}h
                              </TableCell>
                              <TableCell className="font-mono text-amber-600">
                                {(dia.horas_extras_50 + dia.horas_extras_100) > 0 
                                  ? `${(dia.horas_extras_50 + dia.horas_extras_100).toFixed(2)}h` 
                                  : '-'}
                              </TableCell>
                              <TableCell className="font-mono text-purple-600">
                                {dia.adicional_noturno > 0 ? `${dia.adicional_noturno.toFixed(2)}h` : '-'}
                              </TableCell>
                              <TableCell>
                                {dia.atraso_minutos > 0 && (
                                  <Badge variant="outline" className="text-xs text-rose-600 mr-1">
                                    Atraso {dia.atraso_minutos}min
                                  </Badge>
                                )}
                                {dia.inconsistencias?.length > 0 && (
                                  <Badge variant="outline" className="text-xs text-amber-600">
                                    <AlertCircle className="w-3 h-3 mr-1" />
                                    {dia.inconsistencias.length}
                                  </Badge>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-4 text-slate-500">
                <p>Clique em &quot;Salvar e Apurar&quot; para ver o resumo</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Mensagem inicial */}
      {dias.length === 0 && !loading && (
        <Card className="bg-slate-50">
          <CardContent className="py-12 text-center">
            <Clock className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-500">
              Selecione um empregado e o período para iniciar a digitação
            </p>
            <p className="text-sm text-slate-400 mt-1">
              As marcações serão exibidas em formato de grade para edição rápida
            </p>
          </CardContent>
        </Card>
      )}

      {/* Modal de confirmação de reprocessamento */}
      <Dialog open={showConfirmReprocessar} onOpenChange={setShowConfirmReprocessar}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-amber-500" />
              Reprocessar Período
            </DialogTitle>
            <DialogDescription>
              Esta ação irá recalcular todas as jornadas do período selecionado usando as configurações atuais de escalas, horários e faixas de percentuais.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800 font-medium mb-2">O que será reprocessado:</p>
              <ul className="text-sm text-amber-700 space-y-1">
                <li>• Horas trabalhadas, extras e noturnas</li>
                <li>• Verbas e eventos de folha</li>
                <li>• Banco de horas e passivo trabalhista</li>
                <li>• Inconsistências e ocorrências</li>
              </ul>
            </div>
            
            <div className="mt-4 text-sm text-slate-600">
              <p><strong>Empregado:</strong> {empregadoInfo?.nome}</p>
              <p><strong>Período:</strong> {dataInicio} a {dataFim}</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmReprocessar(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={reprocessarPeriodo}
              className="bg-amber-600 hover:bg-amber-700"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Confirmar Reprocessamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DigitacaoRapida;
